'use client';

import { useRouter } from 'next/navigation';
import { useMemo, useRef } from 'react';

import styled from '@emotion/styled';
import { DesignsToken } from '@repo/tokens-studio';
import { NamespaceKeys, useTranslations } from 'next-intl';
import { useResizeObserver } from 'usehooks-ts';

import {
  ALL_BADGE_CODES,
  BadgeCode,
} from '@web/features/badges/catalog';
import {
  countEarned,
  findNextEarnableBadge,
} from '@web/features/badges/progress';
import { useCurrentUser } from '@web/features/user/service';
import { Badge, Chip } from '@web/ui/components';

import { HubTile } from './HubTile';

const BadgesContainer = styled(HubTile)`
  background-color: ${({ theme }) => theme.color.surfaceContainerLow};
  gap: ${({ theme }) => theme.spacing('1500')};
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Label = styled.p`
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  color: ${({ theme }) => theme.color.onSurface};
`;

const PointsLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing('0500')};
  font: ${({ theme }) => theme.typography.mono.regular};
  color: ${({ theme }) => theme.color.onSurfaceVariant};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
`;

const PointsBig = styled.span`
  font: ${({ theme }) => theme.typography.brand.bold};
  font-size: ${({ theme }) => theme.typescale.size['5']};
  color: ${({ theme }) => theme.color.onSurface};
`;

const Grid = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: center;
  min-height: ${({ theme }) => theme.size['6000']};
`;

const BadgeSize = styled.div<{ size: keyof DesignsToken['size'] }>`
  position: absolute;
  width: ${({ theme, size }) => size && theme.size[size]};
`;

export const BadgesHubTile = () => {
  const router = useRouter();
  const { user, status } = useCurrentUser();
  const t = useTranslations();

  const earnedCodes = useMemo(
    () => new Set<string>((user?.badges as string[] | undefined) ?? []),
    [user?.badges],
  );

  const earnedList = useMemo(
    () => ALL_BADGE_CODES.filter((c) => earnedCodes.has(c)),
    [earnedCodes],
  );

  const { earned, total } = countEarned(earnedCodes);

  const nextEarnable = useMemo(
    () => findNextEarnableBadge(earnedCodes, user?.personas ?? []),
    [earnedCodes, user?.personas],
  );

  // Preview list: earned first, then the next-earnable hint, then arbitrary
  // unearned codes to fill the grid.
  const previewList: BadgeCode[] = useMemo(() => {
    const seen = new Set<string>();
    const order: BadgeCode[] = [];
    for (const c of earnedList) {
      if (seen.has(c)) continue;
      order.push(c);
      seen.add(c);
    }
    if (nextEarnable && !seen.has(nextEarnable)) {
      order.push(nextEarnable);
      seen.add(nextEarnable);
    }
    for (const c of ALL_BADGE_CODES) {
      if (seen.has(c)) continue;
      order.push(c);
      seen.add(c);
    }
    return order;
  }, [earnedList, nextEarnable]);

  const badgeRef = useRef<HTMLDivElement | null>(null);
  const badgeSize = useResizeObserver({ ref: badgeRef });

  const gridRef = useRef<HTMLDivElement | null>(null);
  const gridSize = useResizeObserver({ ref: gridRef });
  const visibleBadgeCount = useMemo(() => {
    if (!gridSize.width || !badgeSize.width) return 0;
    return Math.floor((gridSize.width + 8) / (8 + badgeSize.width));
  }, [gridSize, badgeSize]);

  return (
    <BadgesContainer
      background="surfaceContainerHigh"
      stateLayer="onSurface"
      onClick={() => router.push('/badges')}
    >
      <TopBar>
        <Label>{t('HUB_BADGES_TITLE')}</Label>
        <Chip background="inverseSurface" color="onInverseSurface">
          {t('BADGE_OWNED' as NamespaceKeys<string, string>, {
            badgeCount: status === 'pending' ? '-' : earned,
            totalCount: total,
          })}
        </Chip>
      </TopBar>

      <PointsLine>
        <PointsBig>
          {status === 'pending' ? '—' : (user?.badgePoints ?? 0).toLocaleString()}
        </PointsBig>
        <span>{t('BADGES_STATUS_POINTS_LABEL' as NamespaceKeys<string, string>)}</span>
      </PointsLine>

      <Grid ref={gridRef}>
        <BadgeSize ref={badgeRef} size="6000" />
        {previewList.slice(0, visibleBadgeCount).map((code) => (
          <Badge
            key={code}
            size="6000"
            badge={code}
            disabled={status !== 'pending' && !earnedCodes.has(code)}
            tooltip={{ onClick: undefined }}
          />
        ))}
      </Grid>
    </BadgesContainer>
  );
};
